import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import UiContext from '../../../store/uiContext';
import DataContext from '../../../store/dataContext';
import useCartTotalsUtils from '../../../hooks/useCartTotalsUtils';
import { smartLookEvent } from '../../../helpers/analyticsHelper';
import { useSubmitOrderApi } from '../../../hooks/useAxios';
import useTagManager from '../../../hooks/useTagManager';
import useAddressApi from '../../../commons/hooks/use-address-api';
import { selectShipping } from '../../../modules/shipping/slice';
import { selectBilling } from '../../../modules/billing/slice';
import { paymentIdList } from '../../../helpers/constants';
import { useStoreApi } from '../../../hooks/useStoreApi';
import BillingPhone from '../../BillingPhone';
import SubmitBtn from '../../Button/SubmitBtn';
import { getDefaultGoogle } from './default';
import { handleGateway as handleBraintreeGateway } from './braintree';
import { usePickup } from '../../../modules/shipping/containers/pickup-in-store/use-pickup';

import './style.scss';

const GoogleCheckout = ({ setOrderSubmitting, flags }) => {
  const {
    cartId,
    contextShippingAddress,
    contextBillingAddress,
    shipCompany,
    shippingMethodId,
    billingPhone,
    orderNotes,
    email,
    handleOrderSubmit,
    broadcastMessage,
    paymentMethodError,
    isUserSaveInfo,
    portalSettings,
    cvvChanged,
    reset,
    device,
    validNumber,
    shopperCurrency,
    apiPostOrderError,
    applyStoreCredit,
    orderSubmitting,
    cartDetails,
    cartTotals,
    registeredPaymentMethods,
    paymentMethodData,
    additionalCharges,
  } = useContext(DataContext);
  const { updateCheckoutState, TO_DONE, allowBillingAddressAdd } = useContext(UiContext);
  const [error, setError] = useState('');
  const [gatewayData, setGatewayData] = useState(null);
  const { getOrderTotal } = useCartTotalsUtils();
  const { dataLayer } = useTagManager();
  const shipping = useSelector(selectShipping);
  const billing = useSelector(selectBilling);
  const {
    apiResponse: apiGoogleExecuteResponse,
    apiError: apiGoogleExecuteError,
    apiLoading: apiGoogleExecuteLoading,
    apiPostOrder: apiGoogleExecutePost,
  } = useSubmitOrderApi();
  const { gpayKeys, settings } = paymentMethodData?.gpay || paymentMethodData?.googlepay || {};
  const { gateway } = gpayKeys || {};
  const { getWithShippingMethodPayload, executeWithShippingMethod } = useAddressApi();
  const gatewayFunctions = {
    braintree: handleBraintreeGateway,
  };
  const { storeVar } = useStoreApi();
  const { t } = useTranslation();
  const { isPickupActive } = usePickup();

  const isPhoneVisible = () =>
    (apiPostOrderError?.errorCode?.id === 'NF_CHECKOUT_0008' || portalSettings.contactPhone.isVisible) &&
    !allowBillingAddressAdd;

  useEffect(() => {
    const handleSpecificGateway = async () => {
      if (gateway && !gatewayData) {
        gatewayFunctions[gateway](gpayKeys, setGatewayData);
      }
    };
    handleSpecificGateway();
  }, [paymentMethodData]);

  useEffect(() => {
    if ((shippingMethodId || isPickupActive()) && gatewayData && gpayKeys) {
      let billingAddressPayload = {};

      if (isPickupActive()) {
        billingAddressPayload = {
          ...billing.address,
          fullName: shipping.fullName.value,
          line1: billing.address.street,
          line2: billing.address.apartmentUnit,
          postal: billing.address.zip,
          region: billing.address.state,
          regionDescription: billing.address.stateLong,
        };
      } else if (contextBillingAddress && Object.keys(contextBillingAddress).length > 0) {
        billingAddressPayload = contextBillingAddress;
      } else if (billing.address.street) {
        billingAddressPayload = {
          ...billing.address,
          fullName: billing.fullName.value,
          line1: billing.address.street,
          line2: billing.address.apartmentUnit,
          postal: billing.address.zip,
          region: billing.address.state,
          regionDescription: billing.address.stateLong,
        };
      }

      const data = {
        credentials: gpayKeys || {},
        settings: settings || {},
        cartDetails,
        cartTotals,
        shopperCurrency,
        getOrderTotal,
        apiGoogleExecutePost,
        cartId,
        contextShippingAddress,
        contextBillingAddress: billingAddressPayload,
        shipCompany,
        shippingMethodId,
        billingPhone,
        orderNotes,
        email,
        handleOrderSubmit,
        broadcastMessage,
        paymentMethodError,
        isUserSaveInfo,
        portalSettings,
        cvvChanged,
        reset,
        device,
        validNumber,
        apiPostOrderError,
        applyStoreCredit,
        orderSubmitting,
        registeredPaymentMethods,
        paymentMethodData,
        shipping,
        gatewayData,
        storeVar,
        id: 'google-input-wrapper',
        t,
        isPickupActive,
      };
      getDefaultGoogle(data);
    }
  }, [
    paymentMethodData,
    cartTotals,
    shippingMethodId,
    gatewayData,
    isPickupActive,
    billing,
    contextBillingAddress,
    contextShippingAddress,
  ]);

  useEffect(() => {
    if (apiGoogleExecuteResponse?.success) {
      handleOrderSubmit({
        ...apiGoogleExecuteResponse,
        paymentMethod: t('payment_method.googlepay.payed'),
        paymentMethodId: paymentIdList.GOOGLEPAY,
      });
      updateCheckoutState(TO_DONE);
      dataLayer(apiGoogleExecuteResponse, 'Google Pay');
      reset();
      if (!apiGoogleExecuteResponse?.redirectUrl) {
        orderSubmitting(false);
      }
      smartLookEvent('successful-order');
    }
  }, [apiGoogleExecuteResponse]);

  useEffect(() => {
    if (apiGoogleExecuteError) {
      setError(apiGoogleExecuteError);
    }
  }, [apiGoogleExecuteError]);

  useEffect(() => {
    if (apiGoogleExecuteLoading) {
      setOrderSubmitting(apiGoogleExecuteLoading);
    }
  }, [apiGoogleExecuteLoading]);

  useEffect(async () => {
    if (validNumber) {
      const payload = getWithShippingMethodPayload(
        shipping.address,
        shipping.fullName.value,
        cartId,
        shipping.methods.selectedKey,
        shipping.additionalData?.companyName,
        additionalCharges,
      );
      await executeWithShippingMethod(payload);
    }
  }, [validNumber]);

  return (
    <div className="input-wrapper">
      <div id="google-input-wrapper" />
      {isPhoneVisible() && <BillingPhone flags={flags} />}
      {paymentMethodError?.googlepay && <div className="google-error">{paymentMethodError?.googlepay || error}</div>}
      <SubmitBtn
        type={paymentIdList.GOOGLEPAY}
        style={{
          layout: 'horizontal',
          label: 'checkout',
          tagline: 'false',
          marginTop: 0,
          marginBottom: '20px',
        }}
      />
    </div>
  );
};

export default GoogleCheckout;

GoogleCheckout.propTypes = {
  setOrderSubmitting: PropTypes.func.isRequired,
  flags: PropTypes.shape({
    'disable-ddi-phone': PropTypes.bool,
  }),
};
